import { Component, FC, MouseEvent, ReactNode } from 'react';
import { RelayProp, graphql, createFragmentContainer } from 'react-relay';
import { connect, DispatchProp } from 'react-redux';
import { localStorage } from 'dibs-browser-storage';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button, ButtonProps } from 'dibs-elements/exports/Button';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import makeOfferLauncher from 'dibs-buyer-layout/exports/makeOfferLauncher';

// mutations
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems';

// styles
import styles from './PdpSharedButtons.scss';

// helpers
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import {
    trackMakeOfferClick,
    trackMakeOfferFromEmail,
    Location,
} from '../tracking/actionButtonsTracking';
import { setCartItems } from '../../actions/sharedMobileActions';
import { getQueryParam } from '../../utils/uriUtils';

import { PdpSharedMakeOfferButton_item$data } from './__generated__/PdpSharedMakeOfferButton_item.graphql';
import { PdpSharedMakeOfferButton_user$data } from './__generated__/PdpSharedMakeOfferButton_user.graphql';
// constants
const OPEN_OFFER_MODAL = 'openOfferModal';

type DefaultProps = {
    size: 'small' | 'medium' | 'large';
    showArrow: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    shouldLaunchContactDealer?: boolean;
    fullWidth?: boolean;
};

type Props = {
    userId?: string | null;
    itemId: string;
    selectedSkuId?: string;
    item: PdpSharedMakeOfferButton_item$data;
    currency: string;
    relay: RelayProp;
    user: PdpSharedMakeOfferButton_user$data | null;
    quantitySelected?: string;
    gaLabel?: string;
    ctaCopy?: ReactNode;
    ctaType?: ButtonProps['type'];
    location?: Location;
} & DefaultProps &
    DispatchProp;

const MakeOfferCTA: FC<{
    showArrow?: Maybe<boolean>;
    ctaCopy?: ReactNode;
}> = ({ showArrow, ctaCopy }) => (
    <div className={styles.makeOfferButton}>
        {ctaCopy || (
            <FormattedMessage
                id="PdpSharedMakeOfferButton.makeOfferCta"
                defaultMessage="Make An Offer"
            />
        )}
        {showArrow && <ArrowRight className={styles.arrowSVG} />}
    </div>
);

type State = {
    isModalOpen: boolean;
};

class PdpSharedMakeOfferButtonComponentClass extends Component<Props, State> {
    static defaultProps: DefaultProps = {
        size: 'large',
        showArrow: true,
        hasPrivateOffer: false,
        shouldLaunchContactDealer: true,
        fullWidth: true,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            isModalOpen: false,
        };

        this.openModal = this.openModal.bind(this);
    }

    componentDidMount(): void {
        const queryParams = global?.location?.search;
        const modalParam = getQueryParam(queryParams, 'modal');
        const templateParam = getQueryParam(queryParams, 'utm_campaign');
        const openModalFromRefresh = localStorage.getItem(OPEN_OFFER_MODAL);

        if (modalParam === 'offer' || openModalFromRefresh) {
            localStorage.deleteItem(OPEN_OFFER_MODAL);
            if (!this.state.isModalOpen) {
                this.openModal();
            }

            if (templateParam) {
                trackMakeOfferFromEmail(templateParam, {
                    pdpTrackingVpvParams: this.props.item.makeOfferVpvParams,
                });
            }
        }
    }

    openModal(event?: MouseEvent): void {
        const {
            dispatch,
            userId,
            itemId,
            selectedSkuId,
            relay,
            quantitySelected,
            currency,
            hasPrivateOffer,
            shouldLaunchContactDealer,
            gaLabel,
            location,
        } = this.props;
        const { makeOfferVpvParams: pdpTrackingVpvParams, ...item } = this.props.item;
        trackMakeOfferClick({ ...item, pdpTrackingVpvParams }, hasPrivateOffer, event, location);

        if (!userId) {
            // If no user, store item - it can be ADDed to cart when user logs in later
            dispatch(setCartItems(itemId));
        } else {
            // ADD item to user's cart
            this.addToCartMutation(itemId);
        }

        this.setState(
            {
                isModalOpen: true,
            },
            () => {
                makeOfferLauncher({
                    itemId,
                    selectedSkuId,
                    currency,
                    environment: relay.environment,
                    quantitySelected,
                    shouldLaunchContactDealer,
                    launchOrigin: 'pdp-shared',
                    gaLabel,
                    page: 'pdp',
                });
            }
        );
    }

    addToCartMutation(itemId: string): void {
        const { relay, userId } = this.props;
        SharedModifyPortfolioItems.commit(relay.environment, {
            action: 'ADD',
            itemId,
            portfolioType: 'CART',
            userId,
        });
    }

    render(): ReactNode {
        const { size, fullWidth, showArrow, item, ctaCopy, ctaType } = this.props;
        const isPrivateVisibilityType = item?.isPrivateVisibilityType;
        return (
            <Button
                type={ctaType || (isPrivateVisibilityType ? 'secondaryAlt' : 'secondary')}
                className={styles.button}
                dataTn="item-actions-make-offer"
                onClick={this.openModal}
                size={size}
                fullWidth={fullWidth}
            >
                <MakeOfferCTA showArrow={showArrow && size !== 'small'} ctaCopy={ctaCopy} />
            </Button>
        );
    }
}

export const PdpSharedMakeOfferButtonComponent = connect()(PdpSharedMakeOfferButtonComponentClass);

export const PdpSharedMakeOfferButton = createFragmentContainer(PdpSharedMakeOfferButtonComponent, {
    item: graphql`
        fragment PdpSharedMakeOfferButton_item on Item
        @argumentDefinitions(pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: pdp }) {
            ...SharedContactDealerWrapper_item
            ...actionButtonsTracking_item
                @arguments(fetchAuction: $fetchAuction, pageDisplayEnum: $pageDisplayEnum)
            makeOfferVpvParams: pdpTrackingVpvParams(
                userId: $userId
                excludedPurchaseStatuses: [RET, CUST]
            ) @include(if: $isClient)
            isPrivateVisibilityType
        }
    `,
    user: graphql`
        fragment PdpSharedMakeOfferButton_user on User {
            ...SharedContactDealerWrapper_user
            preferences {
                currency
            }
        }
    `,
});
